import type { Object3D } from 'three';
import {
	isSketchArcObject,
	isSketchCircleObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchParabolaObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenArcRelationRuntimeRecord,
	ReferenceDrivenCircleRelationRuntimeRecord,
	ReferenceDrivenEllipseArcRelationRuntimeRecord,
	ReferenceDrivenEllipseRelationRuntimeRecord,
	ReferenceDrivenLineRelationRuntimeRecord,
	ReferenceDrivenParabolaRelationRuntimeRecord,
	ReferenceDrivenSplineRelationRuntimeRecord,
	ReferenceDrivenStyleSplineRelationRuntimeRecord,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import { ReferenceDrivenArcRelationService } from './ReferenceDrivenArcRelationService';
import { ReferenceDrivenCircleRelationService } from './ReferenceDrivenCircleRelationService';
import { ReferenceDrivenEllipseArcRelationService } from './ReferenceDrivenEllipseArcRelationService';
import { ReferenceDrivenEllipseRelationService } from './ReferenceDrivenEllipseRelationService';
import { ReferenceDrivenLineRelationService } from './ReferenceDrivenLineRelationService';
import { ReferenceDrivenParabolaRelationService } from './ReferenceDrivenParabolaRelationService';
import { ReferenceDrivenSplineRelationService } from './ReferenceDrivenSplineRelationService';
import { ReferenceDrivenStyleSplineRelationService } from './ReferenceDrivenStyleSplineRelationService';
import type {
	SketchReferenceRelationAttachDispatcherDependencies,
	SketchReferenceRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceRelationAttachDispatcher';

export type {
	SketchReferenceRelationAttachDispatcherDependencies,
	SketchReferenceRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceRelationAttachDispatcher';

/**
 * 参考关系挂载分发器。
 */
export class SketchReferenceRelationAttachDispatcher {
	private readonly lineRelationService: ReferenceDrivenLineRelationService;
	private readonly circleRelationService: ReferenceDrivenCircleRelationService;
	private readonly ellipseRelationService: ReferenceDrivenEllipseRelationService;
	private readonly arcRelationService: ReferenceDrivenArcRelationService;
	private readonly ellipseArcRelationService: ReferenceDrivenEllipseArcRelationService;
	private readonly parabolaRelationService: ReferenceDrivenParabolaRelationService;
	private readonly splineRelationService: ReferenceDrivenSplineRelationService;
	private readonly styleSplineRelationService: ReferenceDrivenStyleSplineRelationService;

	/**
	 * @param dependencies 分发器依赖。
	 */
	public constructor(
		dependencies: SketchReferenceRelationAttachDispatcherDependencies = {},
	) {
		this.lineRelationService = dependencies.lineRelationService ?? new ReferenceDrivenLineRelationService();
		this.circleRelationService = dependencies.circleRelationService ?? new ReferenceDrivenCircleRelationService();
		this.ellipseRelationService = dependencies.ellipseRelationService ?? new ReferenceDrivenEllipseRelationService();
		this.arcRelationService = dependencies.arcRelationService ?? new ReferenceDrivenArcRelationService();
		this.ellipseArcRelationService = dependencies.ellipseArcRelationService
			?? new ReferenceDrivenEllipseArcRelationService();
		this.parabolaRelationService = dependencies.parabolaRelationService
			?? new ReferenceDrivenParabolaRelationService();
		this.splineRelationService = dependencies.splineRelationService
			?? new ReferenceDrivenSplineRelationService();
		this.styleSplineRelationService = dependencies.styleSplineRelationService
			?? new ReferenceDrivenStyleSplineRelationService();
	}

	/**
	 * 挂载引用关系。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: ProjectionResult;
		targetEntity: Object3D;
		runtimeSourceName?: string;
	}): SketchReferenceRelationAttachResult {
		switch (input.projectionResult.projectionResultKind) {
			case 'line':
				if (!isSketchLineObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.lineRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetLine: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'circle':
				if (!isSketchCircleObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.circleRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetCircle: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'ellipse':
				if (!isSketchEllipseObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.ellipseRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetEllipse: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'arc':
				if (!isSketchArcObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.arcRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetArc: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'ellipse-arc':
				if (!isSketchEllipseObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.ellipseArcRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetEllipse: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'parabola':
				if (!isSketchParabolaObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.parabolaRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetParabola: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'spline':
				if (!isSketchSplineObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.splineRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetSpline: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
			case 'style-spline':
				if (!isSketchStyleSplineObject(input.targetEntity)) {
					return this.createInvalidTargetResult(input.reference, input.reference.targetEntityId);
				}
				return this.styleSplineRelationService.attach({
					reference: input.reference,
					projectionResult: input.projectionResult,
					targetSpline: input.targetEntity,
					runtimeSourceName: input.runtimeSourceName,
				});
		}
	}

	/**
	 * 拆除引用关系。
	 *
	 * @param input 拆除输入。
	 */
	public detach(input: {
		reference: Pick<SketchEntityReferenceRecord, 'referenceId' | 'projectionResultKind'>;
		targetEntity?: Object3D | null;
	}): void {
		switch (input.reference.projectionResultKind) {
			case 'line':
				this.lineRelationService.detach({
					referenceId: input.reference.referenceId,
					targetLine: input.targetEntity && isSketchLineObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'circle':
				this.circleRelationService.detach({
					referenceId: input.reference.referenceId,
					targetCircle: input.targetEntity && isSketchCircleObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'ellipse':
				this.ellipseRelationService.detach({
					referenceId: input.reference.referenceId,
					targetEllipse: input.targetEntity && isSketchEllipseObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'arc':
				this.arcRelationService.detach({
					referenceId: input.reference.referenceId,
					targetArc: input.targetEntity && isSketchArcObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'ellipse-arc':
				this.ellipseArcRelationService.detach({
					referenceId: input.reference.referenceId,
					targetEllipse: input.targetEntity && isSketchEllipseObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'parabola':
				this.parabolaRelationService.detach({
					referenceId: input.reference.referenceId,
					targetParabola: input.targetEntity && isSketchParabolaObject(input.targetEntity)
						? input.targetEntity
						: undefined,
				});
				return;
			case 'spline':
				this.splineRelationService.detach({
					referenceId: input.reference.referenceId,
					targetSpline: input.targetEntity && isSketchSplineObject(input.targetEntity) ? input.targetEntity : undefined,
				});
				return;
			case 'style-spline':
				this.styleSplineRelationService.detach({
					referenceId: input.reference.referenceId,
					targetSpline: input.targetEntity && isSketchStyleSplineObject(input.targetEntity) ? input.targetEntity : undefined,
				});
		}
	}

	/**
	 * 构造目标类型不匹配诊断。
	 *
	 * @param reference 引用记录。
	 * @param targetEntityId 目标实体 id。
	 * @returns 诊断结果。
	 */
	private createInvalidTargetResult(
		reference: SketchEntityReferenceRecord,
		targetEntityId: string,
	): SketchReferenceRelationAttachResult {
		return {
			diagnostics: [
				createExecutionDiagnostic({
					kind: 'projection-invalid',
					level: 'error',
					message: `投影结果与目标实体类型不匹配，无法挂载引用关系: ${reference.referenceId}`,
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId,
				}),
			],
		};
	}
}
